import { Button } from "@design-system/Button/Button";
import { Input } from "@design-system/Input/Input";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import type { ValueControlProps } from "@design-system/formControl/formControl.types";
import { InputSize, toDsInputSize } from "@helpers/form";
import classNames from "classnames";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import {
    ChangeEvent,
    FocusEvent,
    MouseEvent,
    useCallback,
    useMemo,
    useRef,
} from "react";
import { MinusIcon, PlusIcon } from "@icons";
import styles from "./CountInput.module.scss";

interface CountInputProps extends Omit<
    ValueControlProps<number, HTMLInputElement>,
    "value"
> {
    /** An empty value renders a blank input with both buttons disabled. */
    value: number | undefined;
    className?: string;
    min?: number;
    size?: InputSize;
    /** Id of a visible element elsewhere that names the input. */
    "aria-labelledby"?: string;
}

const inputToButtonSize: Record<InputSize, "md" | "lg" | "xl"> = {
    xxs: "md",
    xs: "lg",
    sm: "xl",
    lg: "xl", // we don't have such big size for button so it is handled by styles
};

/**
 * Whole-number input with decrement / increment buttons. `onBlur` fires only
 * when focus leaves the whole control, not when it moves to its buttons.
 */
export const CountInput: React.FC<CountInputProps> = ({
    className,
    value,
    onValueChange,
    onBlur,
    name,
    ref,
    invalid,
    min,
    disabled,
    readOnly,
    required,
    size = "sm",
    "aria-labelledby": ariaLabelledby,
}) => {
    const buttonSize = inputToButtonSize[size];
    const inputRef = useRef<HTMLInputElement | null>(null);
    const mergedRef = useMemo(
        () => mergeRefs([ref, inputRef]),
        [ref, inputRef],
    );
    const areButtonsDisabled = disabled || readOnly || value === undefined;
    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            let newValue = +e.target.value;
            if (isNaN(newValue)) {
                return;
            }

            if (min && newValue < min) {
                newValue = min;
            }
            onValueChange(newValue);
        },
        [onValueChange, min],
    );

    const onSubtract = useCallback(() => {
        if (value === undefined) {
            return;
        }
        onValueChange(value - 1);
    }, [onValueChange, value]);

    const onAdd = useCallback(() => {
        if (value === undefined) {
            return;
        }
        onValueChange(value + 1);
    }, [onValueChange, value]);

    // Stepping with the mouse keeps focus in the input, as Base UI
    // NumberField does; otherwise Safari drops focus to the body and blurs.
    const keepInputFocus = (event: MouseEvent<HTMLButtonElement>) => {
        const isInputFocused =
            inputRef.current !== null &&
            event.currentTarget.ownerDocument.activeElement ===
                inputRef.current;
        if (isInputFocused) {
            event.preventDefault();
        }
    };

    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        const nextFocused = event.relatedTarget;
        const isFocusStayingInside =
            nextFocused instanceof Node &&
            event.currentTarget.contains(nextFocused);
        if (!isFocusStayingInside) {
            onBlur?.();
        }
    };

    return (
        <div
            className={classNames(styles.container, className)}
            onBlur={handleBlur}
        >
            <Button
                type="button"
                onClick={onSubtract}
                onMouseDown={keepInputFocus}
                disabled={areButtonsDisabled || value === min}
                className={classNames(styles.button, styles.leftButton, {
                    [styles.largeButton]: size === "lg",
                })}
                variant="primary"
                size={buttonSize}
            >
                <MinusIcon />
            </Button>
            <Input
                ref={mergedRef}
                name={name}
                disabled={disabled}
                readOnly={readOnly}
                invalid={invalid}
                aria-required={required || undefined}
                {...ariaLabelledbyProps(ariaLabelledby)}
                value={value}
                onChange={handleChange}
                min={min}
                className={styles.input}
                size={toDsInputSize(size)}
                type="number"
            />
            <Button
                type="button"
                onClick={onAdd}
                onMouseDown={keepInputFocus}
                disabled={areButtonsDisabled}
                className={classNames(styles.button, styles.rightButton, {
                    [styles.largeButton]: size === "lg",
                })}
                variant="primary"
                size={buttonSize}
            >
                <PlusIcon />
            </Button>
        </div>
    );
};
